'use client';
import { LinkGroupProps } from '@components/Navigation/_LinkGroup';
import Image from '@repo/ui/src/components/Image/New';
import { Anchor } from '@repo/ui/src/components/Anchor';
import { classNames } from '@repo/ui/src/utils/classNames';
import buildImageSrc from '@repo/ui/src/functions/image-functions-aspect-ratio';
import Accordion, { AccordionItem } from '@repo/ui/src/components/Accordion';
import { navigationClicked } from '@repo/ui/src/services/analytics';

export interface LinkListProps {
  title?: string;
  columns?: Array<LinkGroupProps>;
  primaryHeader?: string;
  secondaryHeader?: string;
}

const LinkList = ({ columns, title, primaryHeader, secondaryHeader }: LinkListProps) => {
  if (!columns?.length) {
    return null;
  }
  const hasTitles = columns?.some((column) => column.title);
  return (
    <div className="tw-grid tw-gap-y-5 sm:tw-gap-y-6 tw-px-4 sm:tw-px-0">
      {title && <h4 className="tra-text-web-sub-2 tw-text-neutral-700">{title}</h4>}
      <div
        className={classNames(
          'tw-grid tw-grid-cols-2 sm:tw-grid-cols-3 tw-gap-6',
          hasTitles && 'tw-hidden sm:tw-grid',
        )}
        data-testid="link-list-desktop-container"
      >
        {columns?.map((column, i) => (
          <div key={i}>
            {column.title && <h5 className="tra-text-web-sub-2 tw-pb-3">{column.title}</h5>}
            <ul className="tw-list-none tw-pl-0 tw-grid tw-gap-y-3">
              {column.links?.map(
                (
                  { link, imageContent, displayName, isExternal, noFollow, noIndex, externalLink },
                  i,
                ) => {
                  const imageUrl = imageContent?.image && buildImageSrc(imageContent);
                  return (
                    <li key={i}>
                      <Anchor
                        link={link}
                        externalLink={externalLink}
                        isExternal={isExternal}
                        noFollow={noFollow}
                        noIndex={noIndex}
                        className={classNames(
                          'tw-text-web-body-l tw-text-neutral-700 tw-transition-all tw-duration-100 tw-flex tw-flex-row tw-items-center tw-gap-3 hover:tw-text-brand-400 hover:tw-underline',
                        )}
                        onClick={() =>
                          navigationClicked({
                            nav_type: 'Top Navigation',
                            clicked_text: displayName ?? '',
                            primary_header: primaryHeader ?? '',
                            secondary_header: secondaryHeader ?? '',
                            tertiary_header: `${title ? title + ' ' : ''}${column.title ? column.title + ' ' : ''}${displayName}`,
                          })
                        }
                      >
                        {imageUrl && (
                          <Image
                            src={buildImageSrc(imageContent)}
                            alt={imageContent?.image?.accessibilityLabel}
                            width={24}
                            height={24}
                            intrinsicSize={imageContent?.static}
                            className="tw-w-6"
                          />
                        )}
                        {displayName}
                      </Anchor>
                    </li>
                  );
                },
              )}
            </ul>
          </div>
        ))}
      </div>
      {hasTitles && (
        <Accordion allowZeroExpanded className="sm:tw-hidden">
          {columns.map((column, i) => (
            <AccordionItem
              key={i}
              heading={column.title ?? `${i}`}
              headerFontClass="tra-text-web-sub-2"
              headerClassName="tw-border-b tw-border-warm-200"
              className="!tw-py-2"
            >
              <ul className="tw-list-none tw-pl-0">
                {column.links?.map(
                  (
                    {
                      link,
                      imageContent,
                      displayName,
                      isExternal,
                      noFollow,
                      noIndex,
                      externalLink,
                    },
                    i,
                  ) => {
                    const imageUrl = imageContent?.image && buildImageSrc(imageContent);
                    return (
                      <li key={i}>
                        <Anchor
                          link={link}
                          externalLink={externalLink}
                          label={displayName}
                          isExternal={isExternal}
                          noFollow={noFollow}
                          noIndex={noIndex}
                          onClick={() =>
                            navigationClicked({
                              nav_type: 'Top Navigation',
                              clicked_text: displayName ?? '',
                              primary_header: primaryHeader ?? '',
                              secondary_header: secondaryHeader ?? '',
                              tertiary_header: `${title ? title + ' ' : ''}${column.title ? column.title + ' ' : ''}${displayName}`,
                            })
                          }
                          className={classNames(
                            'tw-text-web-body-l tw-block tw-text-neutral-700 tw-py-3 tw-px-4',
                          )}
                        >
                          {imageUrl && (
                            <Image
                              src={buildImageSrc(imageContent)}
                              alt={imageContent?.image?.accessibilityLabel}
                              width={24}
                              height={24}
                              intrinsicSize={imageContent?.static}
                            />
                          )}
                          {displayName}
                        </Anchor>
                      </li>
                    );
                  },
                )}
              </ul>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </div>
  );
};

export default LinkList;
